import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { API, StatCard, Field, inputCls, selectCls, Eye, Layers, DollarSign, BarChart3, Plus, Check } from './shared';

export default function EvaluatorsTab() {
  const [evaluators, setEvaluators] = useState([]);
  const [mktStats, setMktStats] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [regAddr, setRegAddr] = useState('');
  const [regName, setRegName] = useState('');
  const [regDomains, setRegDomains] = useState('');
  const [regStake, setRegStake] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regResult, setRegResult] = useState(null);

  const refresh = useCallback(() => {
    fetch(`${API}/api/agent-credit/evaluators/`).then(r => r.json()).then(d => setEvaluators(d.evaluators || [])).catch(() => {});
    fetch(`${API}/api/agent-credit/evaluators/stats/marketplace`).then(r => r.json()).then(setMktStats).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const register = async (e) => {
    e.preventDefault();
    setRegLoading(true); setRegResult(null);
    try {
      const res = await fetch(`${API}/api/agent-credit/evaluators/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_address: regAddr.trim(), name: regName.trim(), domains: regDomains.split(',').map(d => d.trim()).filter(Boolean), staked_kutro: parseFloat(regStake) || 0 }),
      });
      const data = await res.json();
      setRegResult(res.ok ? { type: 'success', msg: `${data.message}. Credibility: ${data.credibility.credibility_score}/100` } : { type: 'error', msg: data.detail });
      if (res.ok) refresh();
    } catch { setRegResult({ type: 'error', msg: 'Network error' }); }
    finally { setRegLoading(false); }
  };

  return (
    <div>
      {mktStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Eye} label="Total Evaluators" value={mktStats.total_evaluators} color="purple" />
          <StatCard icon={Layers} label="USDC Collateral Staked" value={mktStats.total_kutro_staked.toLocaleString()} color="orange" />
          <StatCard icon={DollarSign} label="Fees Earned" value={`$${mktStats.total_fees_earned.toFixed(2)}`} color="green" />
          <StatCard icon={BarChart3} label="Evaluations Done" value={mktStats.total_evaluations} color="blue" />
        </div>
      )}

      <div className="flex justify-end mb-4">
        <Button onClick={() => setShowRegister(!showRegister)} className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:brightness-110" data-testid="register-evaluator-btn">
          <Plus className="w-4 h-4 mr-2" /> Register Evaluator
        </Button>
      </div>

      <AnimatePresence>
        {showRegister && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-8 overflow-hidden">
            <div className="rounded-2xl border border-purple-500/20 bg-purple-500/[0.03] p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><Eye className="w-5 h-5 text-purple-400" /> Register as Evaluator</h3>
              <p className="text-xs text-gray-500 mb-4">Minimum stake: 10,000 USDC. Evaluators assess task quality and earn fees per evaluation.</p>
              <form onSubmit={register} className="grid md:grid-cols-2 gap-4">
                <Field label="Evaluator Address"><input value={regAddr} onChange={e => setRegAddr(e.target.value)} placeholder="0x..." className={inputCls} required data-testid="eval-reg-address" /></Field>
                <Field label="Name"><input value={regName} onChange={e => setRegName(e.target.value)} placeholder="e.g. QualityOracle-3" className={inputCls.replace('font-mono','')} required data-testid="eval-reg-name" /></Field>
                <Field label="Domains (comma separated)"><input value={regDomains} onChange={e => setRegDomains(e.target.value)} placeholder="trading, research, code-generation" className={inputCls.replace('font-mono','')} data-testid="eval-reg-domains" /></Field>
                <Field label="Staked USDC Collateral (min 10,000)"><input value={regStake} onChange={e => setRegStake(e.target.value)} placeholder="10000" type="number" min="10000" className={inputCls} required data-testid="eval-reg-stake" /></Field>
                <div className="md:col-span-2">
                  <Button type="submit" disabled={regLoading} className="w-full bg-gradient-to-r from-purple-600 to-indigo-600" data-testid="eval-reg-submit-btn">
                    {regLoading ? 'Registering...' : 'Register Evaluator'}
                  </Button>
                </div>
              </form>
              {regResult && (
                <div className={`mt-4 p-4 rounded-lg border text-sm ${regResult.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`} data-testid="eval-reg-result">
                  {regResult.msg}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden" data-testid="evaluators-table">
        <div className="px-5 py-4 border-b border-white/5"><h3 className="font-semibold flex items-center gap-2"><Eye className="w-4 h-4 text-purple-400" /> Evaluator Marketplace</h3></div>
        {evaluators.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-xs text-gray-500 uppercase border-b border-white/5">
                <th className="px-4 py-3 text-left">Evaluator</th><th className="px-4 py-3 text-left">Domains</th><th className="px-4 py-3 text-right">Credibility</th><th className="px-4 py-3 text-right">Evaluations</th><th className="px-4 py-3 text-right">Earned</th><th className="px-4 py-3 text-right">Fee/Eval</th>
              </tr></thead>
              <tbody className="divide-y divide-white/[0.03]">
                {evaluators.map(ev => (
                  <tr key={ev.agent_address} className="hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium">{ev.name}</div>
                      <code className="text-[10px] text-gray-600 font-mono">{ev.agent_address?.slice(0,10)}...{ev.agent_address?.slice(-4)}</code>
                    </td>
                    <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{(ev.domains || []).map((d,i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 border border-purple-500/20 text-purple-400">{d}</span>)}</div></td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm font-mono font-bold ${ev.credibility?.credibility_score >= 60 ? 'text-green-400' : ev.credibility?.credibility_score >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {ev.credibility?.credibility_score || 0}/100
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{ev.evaluations_completed}</td>
                    <td className="px-4 py-3 text-right font-mono text-green-400">${ev.total_earned?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono">${ev.credibility?.effective_fee?.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-12 text-center"><Eye className="w-8 h-8 text-gray-700 mx-auto mb-3" /><p className="text-sm text-gray-600">No evaluators registered. Minimum 10,000 USDC stake required.</p></div>
        )}
      </div>
    </div>
  );
}
